import { personLabel } from '../common/person-label.js'
/**
 * The per-entity History routes (PROV-AUDIT).
 *
 * Each detail page has a sibling `…/history` route rendering the same
 * `EntityHistoryPage`; these thin wrappers read the route params and
 * the entity's detail row for its display name. The detail query is
 * cache-first — arriving from the details view costs nothing, and a
 * cold deep link fetches the one row the page would need anyway (the
 * student's row also carries the tenant anchor the timeline query
 * requires).
 */
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { queryKeys } from '../api/query-keys.js'
import { coooriAdminsApi } from '../admins/api.js'
import { adminClientsApi } from '../clients/api.js'
import { adminCohortClient } from '../cohorts/api.js'
import { adminHrUsersApi } from '../hr_users/api.js'
import { adminStudentClient } from '../students/api.js'
import { EntityHistoryPage } from './entity-history-page.js'

export function ClientHistoryPage(): JSX.Element {
  const { clientId = '' } = useParams()
  const detail = useQuery({
    queryKey: queryKeys.client(clientId),
    queryFn: () => adminClientsApi.get(clientId),
  })
  return (
    <EntityHistoryPage
      entityType="client"
      entityId={clientId}
      entityLabel={detail.data?.name}
      entityNoun="this client"
      detailsTo={`/admin/clients/${clientId}`}
    />
  )
}

export function CohortHistoryPage(): JSX.Element {
  const { clientId = '', cohortId = '' } = useParams()
  const detail = useQuery({
    queryKey: queryKeys.cohort(clientId, cohortId),
    queryFn: () => adminCohortClient.get(clientId, cohortId),
  })
  return (
    <EntityHistoryPage
      entityType="cohort"
      entityId={cohortId}
      clientId={clientId}
      entityLabel={detail.data?.name}
      entityNoun="this cohort"
      detailsTo={`/admin/clients/${clientId}/cohorts/${cohortId}`}
    />
  )
}

export function StudentHistoryPage(): JSX.Element {
  const { userId = '' } = useParams()
  const detail = useQuery({
    queryKey: queryKeys.student(userId),
    queryFn: () => adminStudentClient.get(userId),
  })
  return (
    <EntityHistoryPage
      entityType="student"
      entityId={userId}
      // The tenant anchor rides the detail row; the timeline holds
      // until it arrives (EntityHistoryPage gates on it).
      clientId={detail.data?.clientId}
      entityLabel={personLabel(detail.data)}
      entityNoun="this student"
      detailsTo={`/admin/students/${userId}/edit`}
    />
  )
}

export function HrUserHistoryPage(): JSX.Element {
  const { clientId = '', userId = '' } = useParams()
  const detail = useQuery({
    queryKey: queryKeys.hrUser(clientId, userId),
    queryFn: () => adminHrUsersApi.get(clientId, userId),
  })
  return (
    <EntityHistoryPage
      entityType="hr_user"
      entityId={userId}
      clientId={clientId}
      entityLabel={personLabel(detail.data)}
      entityNoun="this HR user"
      detailsTo={`/admin/clients/${clientId}/hr-users/${userId}`}
    />
  )
}

export function CoooriAdminHistoryPage(): JSX.Element {
  const { userId = '' } = useParams()
  const detail = useQuery({
    queryKey: queryKeys.coooriAdmin(userId),
    queryFn: () => coooriAdminsApi.get(userId),
  })
  return (
    // No client anchor on purpose: admin identity rows record each
    // ACTING operator's storage anchor, so the timeline deliberately
    // spans them.
    <EntityHistoryPage
      entityType="admin"
      entityId={userId}
      entityLabel={personLabel(detail.data)}
      entityNoun="this admin"
      detailsTo={`/admin/admins/${userId}`}
    />
  )
}
